import { useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useModal } from '@jutro/components';
import messages from './SpecialtyVehiclesDetails.messages';
import metadata from './SpecialtyVehiclesDetails.metadata.json5';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { ViewModel } from '@xengage/gw-portals-viewmodel-js';
import {ViewModelServiceContext, ViewModelForm, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import PropTypes from 'prop-types';
import styles from './SpecialtyVehiclesDetailsComponent.module.scss';
import appConfig from 'app-config';
import { useTranslator } from '@jutro/locale';
import { LoadSaveService } from 'gw-capability-gateway-quoteandbind';
import { MLModalService } from 'ml-portals-services-react';
import _ from 'lodash';

function SpecialtyVehiclesDetailsComponent(props) {
    const modalApi = useModal();
    const {
        id = 'specialtyVehicleDetails',
        viewModel,
        submissionVM,
        updateWizardData,
        isEditMode,
        setEnableSaveButton,
        setBannerMessage,
    } = props;

    const { refreshData } = useDataRefresh();
    const { authHeader } = useAuthentication();

    const { showErrorModal } = MLModalService();

    const specialtyVehicleVM = viewModel;
    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useTranslator();

    const [productTypeOptions, setProductTypeOptions] = useState([]);
    const [makeOptions, setMakeOptions] = useState([]);
    const [modelOptions, setModelOptions] = useState([]);
    const [years, setYears] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [disableApiCalls, setDisableApiCalls] = useState(false);

    const productSubTypelist = viewModelService.productMetadata.get("pc").types.getTypelist("SpecialtyProductSubType_Ext").codes;
    const productTypelist = viewModelService.productMetadata.get("pc").types.getTypelist("SpecialtyProductType_Ext").codes;
    const displayMakeAndModelTextFieldFor = ["DuneBuggy", "GolfCartLicensed", "GolfCartUnlicensed"]

    const SUBTYPE_TO_PRODUCTTYPE = {
      "Motorcycle": productTypelist.find(pt => pt.code === 'Motorcycle'),
      "ScooterMoped": productTypelist.find(pt => pt.code === 'Motorcycle'),
      "BoatWatercraft": productTypelist.find(pt => pt.code === 'Boat'),
      "PontoonBoat": productTypelist.find(pt => pt.code === 'Boat'),
      "PersonalWatercraft": productTypelist.find(pt => pt.code === 'Boat'),
      "ATVLicensed": productTypelist.find(pt => pt.code === 'OffRoadVehicle'),
      "ATVUnlicensed": productTypelist.find(pt => pt.code === 'OffRoadVehicle'),
      "DuneBuggy": productTypelist.find(pt => pt.code === 'OffRoadVehicle'),
      "GolfCartLicensed": productTypelist.find(pt => pt.code === 'OffRoadVehicle'),
      "GolfCartUnlicensed": productTypelist.find(pt => pt.code === 'OffRoadVehicle'),
      "LowSpeedVehicleLicensed": productTypelist.find(pt => pt.code === 'OffRoadVehicle'),
      "LowSpeedVehicleUnlicensed": productTypelist.find(pt => pt.code === 'OffRoadVehicle'),
      "OffRoadMotorcycle": productTypelist.find(pt => pt.code === 'OffRoadVehicle'),
      "Snowmobile": productTypelist.find(pt => pt.code === 'OffRoadVehicle'),
      "MotorHome": productTypelist.find(pt => pt.code === 'MotorHome'),
      "TrailerUtilityTrailer": productTypelist.find(pt => pt.code === 'TravelTrailer'),
    };

    const group_limit = [
      {
        subtypes: ["OffRoadMotorcycle", "ATVLicensed", "ATVUnlicensed"],
        limit: 10,
      },
      {
        subtypes: ["BoatWatercraft", "PersonalWatercraft"],
        limit: 20,
      },
      {
        subtypes: ["LowSpeedVehicleLicensed", "LowSpeedVehicleUnlicensed"],
        limit: 10
      },
      {
        subtypes: ["GolfCartLicensed", "GolfCartUnlicensed"],
        limit: 10
      }
    ];

    const PRODUCT_LIMIT = {
      "Motorcycle": 10,
      "ScooterMoped": 10,
      "DuneBuggy": 10,
      "Snowmobile": 10,
      "MotorHome": 2,
      "TrailerUtilityTrailer": 2,
      "Pontoon": 10,
    };

    const checkLimitReached = (subtype) => {
      let existingVehicles = _.get(submissionVM, 'value.lobData.personalAuto.specialtyCrossSell_ML.specialtyVehicles', [])
      if(!_.isNil(existingVehicles)){
        // Check group limit first
        const group = group_limit.find(g => g.subtypes.includes(subtype));
        if (group) {
          const groupCount = existingVehicles?.filter(v => group.subtypes.includes(v.productSubType)).length;
          if(groupCount >= group.limit){
              // check for edit mode. If not, disable the button and show error banner msg
             if(!isEditMode){
              setEnableSaveButton(false)
              setBannerMessage(`Limit reached for ${subtype} vehicle type`);
             }else{
                setEnableSaveButton(true)
             }
          }
          else{
            setEnableSaveButton(true)
            setBannerMessage(null)
          }
        }
        else{
          // Individual limit
          const limit = PRODUCT_LIMIT[subtype]
          const count = existingVehicles?.filter(v => v.productSubType === subtype).length;
          if(count >= limit){
            // check for edit mode. If not, disable the button and show error banner msg
            if(!isEditMode){
              setEnableSaveButton(false)
              setBannerMessage(`Limit reached for ${subtype} vehicle type`);
            }else{
              setEnableSaveButton(true)
            }
          }
          else{
            setEnableSaveButton(true)
            setBannerMessage(null)
          }
        }
      }
    };
    
    

    const getProductTypeCodeFromSubType = (subTypeCode) =>
      SUBTYPE_TO_PRODUCTTYPE[(subTypeCode || '').toLowerCase()] || null;

    useEffect(() => {
        const initYears = () => {
            const currentYear = new Date().getUTCFullYear();
            const { oldestCarYear } = appConfig.personalAutoConfig;
            // _.range is not inclusive. Need to add -1 to get correct years
            const yearRange = _.range(currentYear + 1, oldestCarYear - 1);
            return yearRange.map(_.toString).map((year) => ({
              code: year,
              name: year,
            }));
        }
        const productTypes = viewModelService.productMetadata.get("pc").types.getTypelist("SpecialtyProductSubType_Ext").codes;
        const pType = productTypes.map((type) => ({
            code: type.code,
            name: translator({
                id: type.name,
                defaultMessage: type.name
            })
        }))
        setProductTypeOptions(pType);

        let currentSubType = _.get(viewModel, 'productSubType.value.code');
          checkLimitReached(currentSubType);
        setYears(initYears());
    }, [])

    useEffect(() => {
      const setupModelForEdit = async () => {
        // run only for edit mode
        if(isEditMode){
          // check if product is not part of displayMakeAndModelTextFieldFor
          let productType = _.get(specialtyVehicleVM, 'productSubType.value.code');
          if(!displayMakeAndModelTextFieldFor.includes(productType)){
            // populate make and model options
            await fetchMakeOptions();
            await fetchModelOptions();
          }
        }
      }
      setupModelForEdit();
    }, [])



    const writeValue = (value, path) => {
      _.set(specialtyVehicleVM, path, value);
      refreshData();
    };

    const fetchMakeOptions = async() => {
      let productType = _.get(specialtyVehicleVM, 'productSubType.value.code');
      let year = _.get(specialtyVehicleVM, 'year.value');
      let quoteId = submissionVM.quoteID.value;
      if (!productType || !year) {
        return;
      }
      setIsLoading(true);
      try{
        const response = await LoadSaveService.getSpecialtyVehicleMake(quoteId, productType, year, authHeader);
        const makes = _.get(response, 'makes', [])
        const status = _.get(response, 'statusCode')
        if(status === 200 && Array.isArray(makes)){
          const makesObj = makes.map((m) => ({
            code: m.identifier,
            name: m.fullname,
          }))
          setMakeOptions(makesObj);
        } else{
          console.error('Error fetching make options', error);
          showErrorModal('', messages.errorModalMessage)
          setMakeOptions([]);
        }
      } catch (error) {
        console.error('Error fetching make options', error);
        showErrorModal('', messages.errorModalMessage)
        setMakeOptions([]);
      }
      finally{
        setIsLoading(false);
      }
      
    }

    const fetchModelOptions = async() => {
      // clear old model options when fetching new ones
      // setModelOptions([]);
      let productType = _.get(specialtyVehicleVM, 'productSubType.value.code');
      let year = _.get(specialtyVehicleVM, 'year.value');
      let make = _.get(specialtyVehicleVM, 'make.value');
      let quoteId = submissionVM.quoteID.value;
      if (!productType || !year || !make) {
        return;
      }
      setIsLoading(true);
      try{
        const response = await LoadSaveService.getSpecialtyVehicleModel(quoteId, productType, year, make, authHeader);
        if(response.statusCode === 200){
          // MotorVehicle models
          if(!_.isEmpty(response.models.motorVehicles)){
            const modelOptions = response.models.motorVehicles.map((m) => ({
              code: m.model,
              name: m.model,
            }))
            setModelOptions(modelOptions);
          }
          // RecreationalVehicle models
          else if(!_.isEmpty(response.models.recreationalVehicles)){
            const modelOptions = response.models.recreationalVehicles.map((m) => ({
              code: m.modelName,
              name: m.modelName,
            }))
            setModelOptions(modelOptions);
          }
          // Boat models
          else if(!_.isEmpty(response.models.boats)){
            const modelOptions = response.models.boats.map((m) => ({
              code: m.model,
              name: m.model,
            }))
            setModelOptions(modelOptions);
          }
        }
        else{
          console.error('Error fetching make options', error);
          showErrorModal('', messages.errorModalMessage)
          setModelOptions([]);
        }
      }
      catch (error) {
        console.error('Error fetching model options', error);
        showErrorModal('', messages.errorModalMessage)
        setModelOptions([]);
      }
      finally{
        setIsLoading(false);
      }
    }

    const overrideProps = {
      productTypeDropdown: {
        disabled: isLoading || isEditMode,
        availableValues: productTypeOptions,
        onValueChange: (value, path) => {
          if (!_.isEmpty(value)) {
            writeValue(value, path);
            if(!displayMakeAndModelTextFieldFor.includes(value)){
              setDisableApiCalls(false);
              fetchMakeOptions();
            }
            else{
              setDisableApiCalls(true);
            }
            checkLimitReached(value);
            return;
          }
        },
      },
      yearSelector: {
        disabled: isLoading,
        availableValues: years,
        onValueChange: (value, path) => {
          if (!_.isEmpty(value)) {
            writeValue(value, path);
            if(!disableApiCalls){
              fetchMakeOptions();
            }
            return;
          }
        },
      },
      makeSelector: {
        visible: !displayMakeAndModelTextFieldFor.includes(_.get(specialtyVehicleVM, 'productSubType.value.code')),
        disabled: isLoading || _.isEmpty(makeOptions),
        availableValues: makeOptions,
        onValueChange: (value, path) => {
          if (!_.isEmpty(value)) {
            writeValue(value, path);
            fetchModelOptions();
            return;
          }
        },
      },
      modelSelector: {
        visible: !displayMakeAndModelTextFieldFor.includes(_.get(specialtyVehicleVM, 'productSubType.value.code')),
        disabled: isLoading || _.isEmpty(modelOptions),
        availableValues: modelOptions,
        onValueChange: (value, path) => {
          if (!_.isEmpty(value)) {
            writeValue(value, path);
            return;
          }
        },
      },
      makeSelectorTextField: {
        visible: displayMakeAndModelTextFieldFor.includes(_.get(specialtyVehicleVM, 'productSubType.value.code')),
        disabled: isLoading,
        onValueChange: (value, path) => {
          if (_.isUndefined(value)) {
            return;
          }
          writeValue(value, path);
        }
      },
      modelSelectorTextField: {
        visible: displayMakeAndModelTextFieldFor.includes(_.get(specialtyVehicleVM, 'productSubType.value.code')),
        disabled: isLoading,
        onValueChange: (value, path) => {
          if (_.isUndefined(value)) {
            return;
          }
          writeValue(value, path);
        }
      }
    };

    const resolvers = {
      resolveClassNameMap: styles,
      resolveCallbackMap: {
        // onValidate,
        // onVerifyVIN: verifyVIN,
        // onAgentLookupVin: agentLookupVinWithPopup
      },
      resolveComponentMap: {
        // MLAlertGroupComponent
      },
    };

    return (
        <ViewModelForm 
        uiProps={metadata.pageContent}
        model={specialtyVehicleVM}
        overrideProps={overrideProps}
        classNameMap={resolvers.resolveClassNameMap}
        callbackMap={resolvers.resolveCallbackMap}
        componentMap={resolvers.resolveComponentMap}
        />
    );
}

SpecialtyVehiclesDetailsComponent.propTypes = {
    viewModel: PropTypes.shape({}).isRequired,
    submissionVM: PropTypes.shape({}).isRequired,
    updateWizardData: PropTypes.func,
    isEditMode: PropTypes.bool,
    setEnableSaveButton: PropTypes.func.isRequired,
}

SpecialtyVehiclesDetailsComponent.defaultProps = {
    updateWizardData: (value) => {},
    setEnableSaveButton: (value) => {},
}

export default SpecialtyVehiclesDetailsComponent;